/**
 * Deployment progress tracker with step visualization
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, Loader2, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DeploymentStepConfig, StepStatus } from '@/lib/deployment/types';

type Props = {
  steps: DeploymentStepConfig[];
  currentStep: string;
};

const getStatusIcon = (status: StepStatus, isCurrentStep: boolean) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case 'in-progress':
      return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    case 'error':
      return <XCircle className="h-5 w-5 text-red-600" />;
    case 'skipped':
      return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
    default:
      return (
        <Circle
          className={cn(
            'h-5 w-5',
            isCurrentStep ? 'text-primary' : 'text-muted-foreground'
          )}
        />
      );
  }
};

const getStatusColor = (status: StepStatus) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300';
    case 'in-progress':
      return 'bg-primary/10 text-primary';
    case 'error':
      return 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300';
    case 'skipped':
      return 'bg-yellow-100 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export function DeploymentProgress({ steps, currentStep }: Props) {
  const currentIndex = steps.findIndex(s => s.id === currentStep);

  return (
    <Card>
      <CardContent className="p-6">
        <div className="space-y-4">
          {steps.map((step, index) => {
            const isCurrentStep = step.id === currentStep;
            const isPastStep = index < currentIndex;
            const isCompleted = step.status === 'completed';

            return (
              <div key={step.id} className="relative">
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      'absolute left-[10px] top-8 w-0.5 h-12 transition-colors',
                      isPastStep || isCompleted
                        ? 'bg-green-600'
                        : 'bg-border'
                    )}
                  />
                )}
                <div
                  className={cn(
                    'flex items-start gap-4 p-4 rounded-lg transition-all',
                    isCurrentStep && 'bg-primary/5 border-2 border-primary',
                    !isCurrentStep && 'border border-border'
                  )}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {getStatusIcon(step.status, isCurrentStep)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4
                        className={cn(
                          'font-medium',
                          isCurrentStep && 'text-primary'
                        )}
                      >
                        {step.title}
                      </h4>
                      {step.status !== 'pending' && (
                        <Badge
                          variant="outline"
                          className={cn('text-xs capitalize', getStatusColor(step.status))}
                        >
                          {step.status === 'in-progress' ? 'In Progress' : step.status}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
